import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { AiOutlineAppstoreAdd } from "react-icons/ai";
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';

import ChannelsList from './ChannelsList';
import LayoutList from './LayoutList';
import PlayLists from './PlayLists';
import MediaList from './MediaList';
import LinksList from './LinksList';
import AppsList from './AppsList';
import YouTubeSearchList from './YouTubeSearchList';

import { setAssign, setSelectedApp, setType, toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import { useScrollLoadingContext } from '../../Contexts/ScrollLoadingContext';
import AnimatedLayoutList from './AnimatedLayoutList';
import HdmiList from './HdmiList';
import './AddContentPopup.css';

function AddContentPopup({
  // Layer-3 campaign builder additions (additive-only; every existing
  // caller passes none of these and gets 100% identical behavior):
  //  - simplifiedMainMode: MAIN-content "إضافة محتوى" — only the 4 primary
  //    sources render as real tabs (playlist/media/youtube/links, each
  //    keeping its own existing visibility rule); layout/animated_layouts/
  //    apps are dropped from THIS view entirely (simplify), and
  //    channels/hdmi always render but DISABLED with a visible reason
  //    instead of vanishing when the tenant's package doesn't cover them.
  //  - restrictToPlaylistOnly: الصور (أجواء) picker — only the playlist
  //    tab's content renders, no tab bar at all (reuses the exact existing
  //    "attach an existing company playlist to a layer" mechanism).
  //  - titleOverride/subtitleOverride: header copy for the above.
  //  - onAfterHide: fires after the dialog closes (used by the vibes
  //    picker to restore the campaign's `layer` redux state to MAIN).
  //  - playlistSheetMode: the standalone playlist page's (`/playlist/:id/
  //    show`) own add-content sheet. Unlike simplifiedMainMode/
  //    restrictToPlaylistOnly (both campaign-builder-only), this mode's
  //    available tabs depend on the PLAYLIST's own `content_layer`
  //    (redux `layer`, already set from `playlistInfo.content_layer` by
  //    PlaylistSetup.jsx) rather than a fixed key list: MAIN gets the same
  //    tab set as simplifiedMainMode (media incl. audio/youtube/links/
  //    channels/hdmi, each still gated by its own existing `visible` rule
  //    below — nothing new invented); non-MAIN (PLAYGROUND/CORNER/BANNER)
  //    gets media-only, mirroring restrictToPlaylistOnly's own narrower
  //    vibes set (minus the playlist tab, which a playlist can never embed
  //    another playlist into — proven 422 on `campaign/content/assign`
  //    with a playlist id as campaign_id, and no DB column for it either).
  simplifiedMainMode = false,
  restrictToPlaylistOnly = false,
  playlistSheetMode = false,
  titleOverride = null,
  subtitleOverride = null,
  onAfterHide = null,
} = {}) {
  const { t } = useTranslation();
  const { layer, hideTabs, openDialog, assign, companyDetails } = useSelector((state) => state.global);
  const { ids } = useScrollLoadingContext();
  const dispatch = useDispatch();
  const { setIds } = useScrollLoadingContext();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isBuilderSheet = simplifiedMainMode || restrictToPlaylistOnly || playlistSheetMode;
  // Non-MAIN playlist content, like the vibes picker, is images/video only
  // (no audio — vibes layers are muted overlay content, not a music track).
  const isNonMainPlaylistSheet = playlistSheetMode && layer !== 'MAIN';

  // Slide-in transition — mount hidden, flip to .show a tick later (the
  // prototype's own scrim/sheet opacity+transform transition).
  const [sheetShow, setSheetShow] = useState(false);
  useEffect(() => {
    if (!isBuilderSheet) return;
    const raf = requestAnimationFrame(() => setSheetShow(true));
    return () => cancelAnimationFrame(raf);
  }, [isBuilderSheet]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      key: "playlist",
      title: t('addcontent.tab_playlist'),
      child: <PlayLists builderRow={isBuilderSheet} />,
      visible:
        layer !== "BANNER" &&
        (window.location.pathname.includes("campaign") ||
          window.location.pathname.includes("layout")) &&
        !hideTabs &&
        // A playlist's own content can never reference another playlist
        // (POST /campaign/content/assign with a playlist id as campaign_id
        // 422s "The selected campaign id is invalid"; playlist_media has no
        // column for it either) — this tab must never show on the playlist
        // page's own add-content sheet.
        !playlistSheetMode,
      icon: <i className="bx bxs-playlist"></i>,
    },
    {
      key: "media",
      title: t('addcontent.tab_media'),
      // vibesMode hides audio (images/video only) — used both by the real
      // vibes picker (restrictToPlaylistOnly) and, additively, by a
      // non-MAIN playlist's own add-content sheet (PLAYGROUND/CORNER/BANNER
      // content is muted overlay content, same reasoning as vibes).
      child: <MediaList builderRow={isBuilderSheet} vibesMode={restrictToPlaylistOnly || isNonMainPlaylistSheet} />,
      visible: true,
      icon: <i className="bx bx-images"></i>,
    },
    {
      key: "youtube_search",
      title: t('addcontent.tab_youtube_search'),
      child: <YouTubeSearchList builderRow={isBuilderSheet} />,
      visible:
        companyDetails?.package?.features?.["media_type"]?.includes("youtube") &&
        layer !== "BANNER",
      icon: <i className="bx bxl-youtube"></i>,
    },
    {
      key: "links",
      title: t('addcontent.tab_links'),
      child: <LinksList builderRow={isBuilderSheet} />,
      visible:
        companyDetails?.package?.features?.["media_type"]?.includes("wep", "livestream", "youtube") &&
        layer !== "BANNER",
      icon: <i className="bx bx-link"></i>,
    },
    {
      key: "layout",
      title: t('addcontent.tab_layout'),
      child: <LayoutList />,
      visible:
        companyDetails?.package?.features?.["layouts"] &&
        ["MAIN", "PLAYGROUND"].includes(layer) &&
        window.location.pathname.includes("campaign") &&
        !hideTabs,
      icon: <i className="bx bxs-layout"></i>,
    },
    {
      key: "channels",
      title: t('addcontent.tab_channels'),
      child: <ChannelsList />,
      visible:
        companyDetails?.package?.features?.["media_type"]?.includes("satellite") &&
        !hideTabs &&
        ["MAIN", "PLAYGROUND"].includes(layer),
      // Underlying availability, separate from `visible` above — used only
      // in simplifiedMainMode, where this tab always renders and shows WHY
      // it's disabled instead of vanishing.
      available: !!companyDetails?.package?.features?.["media_type"]?.includes("satellite"),
      disabledReason: t('show.show_tab_channels_reason'),
      icon: <i className="bx bx-tv"></i>,
    },
    {
      key: "animated_layouts",
      title: t('addcontent.tab_animated'),
      child: <AnimatedLayoutList />,
      visible:
        companyDetails?.package?.features?.["animated_layouts"] &&
        ["MAIN", "PLAYGROUND"].includes(layer) &&
        window.location.pathname.includes("campaign") &&
        !hideTabs,
      icon: <i className="fa-solid fa-wand-magic-sparkles"></i>,
    },
    {
      key: "apps",
      title: t('addcontent.tab_apps'),
      child: <AppsList />,
      visible: true,
      icon: <AiOutlineAppstoreAdd />,
    },
    {
      key: "hdmi",
      title: t('addcontent.tab_hdmi'),
      child: <HdmiList />,
      visible:
        companyDetails?.package?.features?.["media_type"]?.includes("hdmi") && layer !== "BANNER",
      available: !!companyDetails?.package?.features?.["media_type"]?.includes("hdmi"),
      disabledReason: t('show.show_tab_hdmi_reason'),
      icon: <i className="bx bx-link"></i>,
    },
  ];

  // simplifiedMainMode: only the 4 primary sources + channels/hdmi (shown
  // disabled-with-reason instead of vanishing). layout/animated_layouts/
  // apps are intentionally out of scope for this simplified view.
  const SIMPLIFIED_KEYS = ["playlist", "media", "youtube_search", "links", "channels", "hdmi"];
  // restrictToPlaylistOnly (the vibes sheet) now shows TWO tabs — media
  // first, then playlist — instead of the old playlist-only single view.
  // The prop name is kept (13+ non-builder callers never pass it) but what
  // it filters to has changed.
  const VIBES_KEYS = ["media", "playlist"];
  // playlistSheetMode: MAIN playlists get the same real-tab set as
  // simplifiedMainMode minus "playlist" (structurally excluded for every
  // playlist layer above, a playlist can't reference another playlist);
  // non-MAIN (PLAYGROUND/CORNER/BANNER) gets media-only, mirroring
  // VIBES_KEYS minus "playlist".
  const PLAYLIST_SHEET_KEYS_MAIN = ["media", "youtube_search", "links", "channels", "hdmi"];
  const PLAYLIST_SHEET_KEYS_OTHER = ["media"];
  const effectiveMenuItems = simplifiedMainMode
    ? menuItems
        .filter((m) => SIMPLIFIED_KEYS.includes(m.key))
        .map((m) =>
          "available" in m
            ? { ...m, visible: true, disabled: !m.available }
            : { ...m, disabled: false }
        )
    : restrictToPlaylistOnly
    ? menuItems.filter((m) => VIBES_KEYS.includes(m.key))
    : playlistSheetMode
    ? menuItems
        .filter((m) =>
          (isNonMainPlaylistSheet ? PLAYLIST_SHEET_KEYS_OTHER : PLAYLIST_SHEET_KEYS_MAIN).includes(m.key)
        )
        .map((m) =>
          "available" in m
            ? { ...m, visible: true, disabled: !m.available }
            : { ...m, disabled: false }
        )
    : menuItems;

  // Explicit tab ORDER per sheet kind — independent of `menuItems`' own
  // array order (which every non-builder caller's sidebar/mobile-grid still
  // reads unsorted, unaffected by this). Anything not listed keeps its
  // relative position at the end.
  const TAB_ORDER_MAIN = ["media", "playlist", "youtube_search", "links", "channels", "hdmi"];
  const TAB_ORDER_VIBES = ["media", "playlist"];
  const TAB_ORDER_PLAYLIST_MAIN = ["media", "youtube_search", "links", "channels", "hdmi"];
  const TAB_ORDER_PLAYLIST_OTHER = ["media"];
  const tabOrder = simplifiedMainMode
    ? TAB_ORDER_MAIN
    : restrictToPlaylistOnly
    ? TAB_ORDER_VIBES
    : playlistSheetMode
    ? (isNonMainPlaylistSheet ? TAB_ORDER_PLAYLIST_OTHER : TAB_ORDER_PLAYLIST_MAIN)
    : null;
  if (tabOrder) {
    effectiveMenuItems.sort((a, b) => {
      const ia = tabOrder.indexOf(a.key);
      const ib = tabOrder.indexOf(b.key);
      return (ia === -1 ? tabOrder.length : ia) - (ib === -1 ? tabOrder.length : ib);
    });
  }

  // Default to "media" if visible, otherwise first visible item
  const firstVisible = effectiveMenuItems.find((m) => m.visible && !m.disabled) ?? effectiveMenuItems[0];
  const [activeTab, setActiveTap] = useState(
    effectiveMenuItems.find((m) => m.key === "media" && m.visible) ?? firstVisible
  );

  const assignMedia = () => dispatch(setAssign(true));

  // Desktop footer
  const renderFooter = () => (
    <div className="w-100 d-flex gap-2 md:flex-row md:justify-end md:align-items-center">
      {ids.length > 0 && (
        <div className="d-flex align-items-center text-black fw-medium">
          <span className="me-2">{t('addcontent.selected')}</span>
          <span className="bg-black text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 20, height: 20, fontSize: 12 }}>
            {ids.length}
          </span>
        </div>
      )}

      <Button
        icon="pi pi-plus"
        label={t('buttons.add')}
        loading={assign}
        className="btn btn-primary text-center fs-6 p-2"
        onClick={assignMedia}
        disabled={
          activeTab?.key === "youtube_search" ||
          (activeTab?.key !== "channels" && ids?.length === 0)
        }
      />
    </div>
  );

  const onHideHandler = () => {
    dispatch(setSelectedApp({}));
    dispatch(toggleDialog("addContent"));
    onAfterHide?.();
  };

  useEffect(() => {
    localStorage.setItem("folder_id", "");
  }, []);

  useEffect(() => {
    setIds([]);
  }, [activeTab, setIds]);

  // Desktop Tab Button — `item.disabled` (simplifiedMainMode only) renders
  // the tab inert with its reason shown, instead of vanishing.
  const TabButton = ({ item }) => (
    <button
      key={item.key}
      disabled={item?.disabled}
      className={`py-2 px-3 border-0 text-start d-flex align-items-center whitespace-nowrap
        ${item?.key === activeTab?.key ? "active_upload_button" : "bg-transparent"}
        ${item?.disabled ? "opacity-50" : ""}
        `}
      onClick={() => {
        if (item?.disabled) return;
        dispatch(setType(item?.key));
        setActiveTap(item);
      }}
    >
      <span className="menu-icon me-2">{item?.icon}</span>
      <span className="d-flex flex-column">
        <span>{item?.title}</span>
        {item?.disabled && item?.disabledReason && (
          <span className="pp-cb-tab-disabled-reason">{item.disabledReason}</span>
        )}
      </span>
    </button>
  );

  // Mobile Grid Tab Button - More touch friendly
  const MobileTabButton = ({ item }) => (
    <button
      key={item.key}
      disabled={item?.disabled}
      className={`mobile-tab-btn ${item?.key === activeTab?.key ? "mobile-tab-active" : ""} ${item?.disabled ? "opacity-50" : ""}`}
      onClick={() => {
        if (item?.disabled) return;
        dispatch(setType(item?.key));
        setActiveTap(item);
      }}
    >
      <span className="mobile-tab-icon">{item?.icon}</span>
      <span className="mobile-tab-label">{item?.title}</span>
      {item?.disabled && item?.disabledReason && (
        <span className="pp-cb-tab-disabled-reason">{item.disabledReason}</span>
      )}
    </button>
  );

  const visibleItems = effectiveMenuItems.filter((i) => i.visible);
  // Both builder sheet kinds now show a visible tab strip — vibes went from
  // a single hidden-strip playlist-only view to a real 2-tab (media/playlist)
  // strip. Non-builder callers never reach this component's isBuilderSheet
  // branch at all, so this only affects simplifiedMainMode/restrictToPlaylistOnly.
  const showTabStrip = true;

  // ═══════════════════════════════════════════════════════════════════
  // Layer-3 campaign builder — bottom sheet, ported pixel-for-pixel from
  // .claude/pp-campaign-builder-prototype.html: full-width bottom sheet on
  // mobile, centered floating sheet (520px, left:50%+translateX(-50%)) on
  // desktop — ONE DOM tree, no isMobile branch; the positioning is a pure
  // CSS media query exactly like the prototype. Every other caller of this
  // component (PlaylistSetup, InteractiveCampaignSetup, ScreenInfo, ...)
  // never sets simplifiedMainMode/restrictToPlaylistOnly and keeps the
  // original PrimeReact Dialog below, completely untouched.
  // ═══════════════════════════════════════════════════════════════════
  if (isBuilderSheet) {
    const footerDisabled = activeTab?.key === "youtube_search" || activeTab?.key === "links" ||
      (activeTab?.key !== "channels" && ids?.length === 0);
    const showFooter = activeTab?.key !== "youtube_search" && activeTab?.key !== "links";

    return (
      <>
        <div className={`pp-cb-scrim ${sheetShow ? 'show' : ''}`} onClick={onHideHandler}></div>
        <div className={`pp-cb-sheet ${sheetShow ? 'show' : ''}`} role="dialog" aria-label={titleOverride ?? t('addcontent.title_main')}>
          <div className="pp-cb-grabber"></div>
          <h3>{titleOverride ?? t('addcontent.title_main')}</h3>
          <div className="pp-cb-sheet-sub">{subtitleOverride ?? t('addcontent.subtitle_main')}</div>

          {showTabStrip && (
            <div className="pp-cb-tabs">
              {effectiveMenuItems.filter((i) => i.visible).map((item) => (
                <button
                  key={item.key}
                  className={`pp-cb-tab ${item.key === activeTab?.key ? 'on' : ''}`}
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) return;
                    dispatch(setType(item.key));
                    setActiveTap(item);
                  }}
                >
                  <span>{item.title}</span>
                  {item.disabled && item.disabledReason && (
                    <span className="lock">{item.disabledReason}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="pp-cb-sheet-body">
            {activeTab?.child}
          </div>

          {showFooter && (
            <div className="d-flex gap-2 align-items-center" style={{ marginTop: 14, flex: '0 0 auto' }}>
              <Button
                icon="pi pi-plus"
                label={ids.length > 0 ? t('addcontent.add_count', { count: ids.length }) : t('buttons.add')}
                loading={assign}
                className="flex-1"
                onClick={assignMedia}
                disabled={footerDisabled}
              />
            </div>
          )}

          <Button
            outlined
            label={t('buttons.cancel')}
            className="w-100"
            style={{ marginTop: 10 }}
            onClick={onHideHandler}
          />
        </div>
      </>
    );
  }

  // Mobile view - Bottom sheet style
  if (isMobile) {
    return (
      <Dialog
        header={
          <div className="mobile-header">
            <span className="mobile-header-title">{titleOverride ?? t('addcontent.title')}</span>
            {ids.length > 0 && (
              <span className="mobile-selection-badge">{ids.length} selected</span>
            )}
          </div>
        }
        visible={openDialog.addContent}
        onHide={onHideHandler}
        position="bottom"
        className="mobile-add-content-dialog"
        draggable={false}
        resizable={false}
        blockScroll
        footer={null}
      >
        <div className="mobile-content-wrapper">
          {subtitleOverride && (
            <div className="pp-cb-sec-head" style={{ marginBottom: 8 }}>
              <div className="pp-cb-desc">{subtitleOverride}</div>
            </div>
          )}
          {/* Mobile Tab Grid */}
          {showTabStrip && (
            <div className="mobile-tabs-grid">
              {visibleItems.map((item) => (
                <MobileTabButton key={item.key} item={item} />
              ))}
            </div>
          )}

          {/* Content area with better scroll */}
          <div className="mobile-content-area">
            {activeTab?.child}
          </div>

          {/* Sticky Mobile Footer */}
          <div className="mobile-sticky-footer">
            <Button
              icon="pi pi-plus"
              label={ids.length > 0 ? t('addcontent.add_items', { count: ids.length }) : t('buttons.add')}
              loading={assign}
              className="mobile-add-btn"
              onClick={assignMedia}
              disabled={
                activeTab?.key === "youtube_search" ||
                (activeTab?.key !== "channels" && ids?.length === 0)
              }
            />
          </div>
        </div>
      </Dialog>
    );
  }

  // Desktop view - Original layout
  return (
    <>
      <Dialog
        header={
          subtitleOverride ? (
            <div>
              <div>{titleOverride ?? t('addcontent.title')}</div>
              <div className="pp-cb-desc" style={{ fontWeight: 400 }}>{subtitleOverride}</div>
            </div>
          ) : (
            titleOverride ?? t('addcontent.title')
          )
        }
        visible={openDialog.addContent}
        onHide={onHideHandler}
        footer={renderFooter}
        className="pp-add-content-dialog"
        breakpoints={{
          "1120px": "75vw",
          "960px": "85vw",
          "820px": "92vw",
          "480px": "96vw",
        }}
        style={{ width: "65vw", height: "80vh", maxHeight: "80vh" }}
        blockScroll
      >
        <div className="d-flex flex-row gap-0" style={{ height: '100%', minHeight: 0 }}>
          {/* Desktop sidebar */}
          {showTabStrip && (
            <div
              className="d-none d-md-block px-0 bg-white media_options_container border-end"
              style={{ width: 185, flexShrink: 0, overflowY: 'auto' }}
            >
              {visibleItems.map((item) => (
                <div key={item.key} className={`w-100 ${item?.key === activeTab?.key ? "active_upload_button" : "bg-transparent"}`}>
                  <TabButton item={item} />
                </div>
              ))}
            </div>
          )}

          {/* Content area — scrolls independently of header / sidebar */}
          <div
            className="flex-1 w-100 p-0 min-w-0 pp-add-content-scroll"
            style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
          >
            {activeTab?.child}
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AddContentPopup;
